import axios from "../../api/axiosInstance";
import { STATUS, getProjectId, extractErrorInfo } from "./sharedfunction";

/**
 * Fetch execution file folders for a project in a hierarchical tree or flat list.
 * GET /api/:projectId/execution-file-folders
 *
 * Each folder in the response includes:
 * - fileCount: number of files directly in that folder
 * - totalFileCount: fileCount + files in all descendant folders
 *
 * @param {Object} [params] - Optional query parameters
 * @param {string} [params.levels] - Comma-separated level numbers to include ("0", "1", "2", "0,1", etc.)
 * @param {string} [params.parentFolderId] - UUID — returns only direct children of this parent
 * @param {string} [params.folderName] - Case-insensitive partial match on folder name
 * @param {boolean} [params.flat] - When true, returns a flat array instead of a nested tree
 * @returns {Promise<{status: number, data?: Array, error?: string, message?: string}>}
 */
export async function getExecutionFileFolders({
  levels,
  parentFolderId,
  folderName,
  flat,
} = {}) {
  try {
    const projectId = getProjectId();

    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    const queryParams = {};
    if (levels !== undefined) queryParams.levels = levels;
    if (parentFolderId !== undefined) queryParams.parentFolderId = parentFolderId;
    if (folderName !== undefined) queryParams.folderName = folderName;
    if (flat !== undefined) queryParams.flat = flat;

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/execution-file-folders`,
      {
        params: queryParams,
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || [],
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching execution file folders:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Create a new execution file folder within a project.
 * POST /api/:projectId/execution-file-folders
 *
 * Hierarchy rules (3-level max):
 * - No parentFolderId → Root folder (level 0)
 * - parentFolderId is a Root folder → Sub Folder (level 1)
 * - parentFolderId is a Sub Folder → Leaf folder (level 2)
 * - parentFolderId is a Leaf folder → rejected (max depth exceeded)
 *
 * @param {Object} params - Parameters
 * @param {string} params.folderName - Folder name (unique under same parent, 1–100 chars)
 * @param {string} [params.folderDescription] - Optional folder description (max 500 chars)
 * @param {string|null} [params.parentFolderId] - Parent folder UUID, or null/omit for a root folder
 * @returns {Promise<{status: number, data?: Object, error?: string, message?: string}>}
 */
export async function createExecutionFileFolder({
  folderName,
  folderDescription,
  parentFolderId,
} = {}) {
  try {
    const projectId = getProjectId();

    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!folderName || folderName.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Folder name is required",
      };
    }

    const payload = {
      folderName: folderName.trim(),
      ...(folderDescription !== undefined && { folderDescription }),
      ...(parentFolderId !== undefined && { parentFolderId }),
    };

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/${projectId}/execution-file-folders`,
      payload,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error creating execution file folder:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Update an existing execution file folder's name and/or description.
 * PUT /api/:projectId/execution-file-folders/:folderId
 *
 * Both fields are optional — only provided fields are updated.
 * If folderName is provided it must be unique under the same parent (case-insensitive).
 *
 * @param {Object} params - Parameters
 * @param {string} params.folderId - Folder UUID to update
 * @param {string} [params.folderName] - New folder name (max 100 chars)
 * @param {string|null} [params.folderDescription] - New folder description (max 500 chars)
 * @returns {Promise<{status: number, data?: Object, error?: string, message?: string}>}
 */
export async function updateExecutionFileFolder({
  folderId,
  folderName,
  folderDescription,
} = {}) {
  try {
    const projectId = getProjectId();

    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!folderId) {
      return {
        status: STATUS.ERROR,
        error: "Folder ID is required",
      };
    }

    const payload = {};
    if (folderName !== undefined) payload.folderName = folderName;
    if (folderDescription !== undefined) payload.folderDescription = folderDescription;

    if (Object.keys(payload).length === 0) {
      return {
        status: STATUS.ERROR,
        error: "At least one field (folderName or folderDescription) must be provided",
      };
    }

    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/${projectId}/execution-file-folders/${folderId}`,
      payload,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error updating execution file folder:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Soft-delete an execution file folder and all its descendant subfolders recursively.
 * DELETE /api/:projectId/execution-file-folders/:folderId
 *
 * @param {Object} params - Parameters
 * @param {string} params.folderId - Folder UUID to delete
 * @returns {Promise<{status: number, data?: {deletedCount: number, deletedIds: string[]}, error?: string, message?: string}>}
 */
export async function deleteExecutionFileFolder({ folderId } = {}) {
  try {
    const projectId = getProjectId();

    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!folderId) {
      return {
        status: STATUS.ERROR,
        error: "Folder ID is required",
      };
    }

    const response = await axios.delete(
      `${process.env.REACT_APP_API_URL}/${projectId}/execution-file-folders/${folderId}`,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error deleting execution file folder:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Move an execution file folder to a new parent or to root level.
 * PATCH /api/:projectId/execution-file-folders/:folderId/move
 *
 * Safety checks enforced by the backend:
 * - Cannot move a folder into itself
 * - Cannot move a folder into one of its own descendants (circular reference)
 * - Folder + subtree must fit within 3-level hierarchy at the target location
 * - Duplicate name check in the target location (case-insensitive)
 *
 * @param {Object} params - Parameters
 * @param {string} params.folderId - Folder UUID to move
 * @param {string|null} params.parentFolderId - Target parent folder UUID, or null to move to root
 * @returns {Promise<{status: number, data?: Object, error?: string, message?: string}>}
 */
export async function moveExecutionFileFolder({ folderId, parentFolderId } = {}) {
  try {
    const projectId = getProjectId();

    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!folderId) {
      return {
        status: STATUS.ERROR,
        error: "Folder ID is required",
      };
    }

    if (parentFolderId === undefined) {
      return {
        status: STATUS.ERROR,
        error: "parentFolderId is required (use null to move to root)",
      };
    }

    const response = await axios.patch(
      `${process.env.REACT_APP_API_URL}/${projectId}/execution-file-folders/${folderId}/move`,
      { parentFolderId },
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error moving execution file folder:", error);
    return extractErrorInfo(error);
  }
}
